import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'Admin' | 'User' | 'Validator' | 'ReadOnly' | 'Builder' | 'Trader';
  adminOnly?: boolean;
  fallback?: ReactNode;
  redirectTo?: string;
}

// Role hierarchy for access control
const ROLE_HIERARCHY: Record<string, number> = {
  'ReadOnly': 1,
  'User': 2,
  'Trader': 2,
  'Builder': 3,
  'Validator': 3,
  'Admin': 4,
};

// Check if user has required access level
const hasRequiredAccess = (userRole: string, requiredRole?: string, adminOnly?: boolean): boolean => {
  if (adminOnly) {
    return userRole === 'Admin';
  }

  if (!requiredRole) {
    return true; // No specific role required
  }

  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;

  return userLevel >= requiredLevel;
};

// Loading component
const LoadingScreen: React.FC = () => (
  <div className="flex flex-col justify-center items-center min-h-[400px] gap-4">
    <Loader2 className="h-12 w-12 animate-spin text-primary" />
    <h2 className="text-xl font-semibold text-muted-foreground">
      Authenticating...
    </h2>
  </div>
);

// Access denied component
const AccessDeniedScreen: React.FC<{ requiredRole?: string; adminOnly?: boolean }> = ({
  requiredRole,
  adminOnly
}) => (
  <div className="flex flex-col justify-center items-center min-h-[400px] gap-4 p-4">
    <Alert variant="destructive" className="w-full max-w-[500px]">
      <AlertTitle className="text-lg font-semibold">Access Denied</AlertTitle>
      <AlertDescription className="mt-2">
        You don't have the required permissions to access this page.
        {adminOnly && ' This page is restricted to administrators only.'}
        {requiredRole && !adminOnly && ` Required role: ${requiredRole} or higher.`}
      </AlertDescription>
    </Alert>
    <p className="text-sm text-muted-foreground">
      If you believe this is an error, please contact your administrator.
    </p>
  </div>
);

// Error boundary for auth errors
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class AuthErrorBoundary extends React.Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Auth Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col justify-center items-center min-h-[400px] gap-4 p-4">
          <Alert variant="destructive" className="w-full max-w-[500px]">
            <AlertTitle className="text-lg font-semibold">Authentication Error</AlertTitle>
            <AlertDescription className="mt-2">
              Something went wrong with authentication. Please try refreshing the page or logging in again.
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main protected route component
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  adminOnly = false,
  fallback,
  redirectTo = '/login',
}) => {
  const { user, isAuthenticated, isLoading, error } = useAuth();
  const location = useLocation();

  // Show loading screen while checking authentication
  if (isLoading) {
    return <>{fallback || <LoadingScreen />}</>;
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px] gap-4 p-4">
        <Alert variant="destructive" className="w-full max-w-[500px]">
          <AlertTitle className="text-lg font-semibold">Authentication Error</AlertTitle>
          <AlertDescription className="mt-2">{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check role-based access
  if (!hasRequiredAccess(user.role, requiredRole, adminOnly)) {
    return (
      <AccessDeniedScreen
        requiredRole={requiredRole}
        adminOnly={adminOnly}
      />
    );
  }

  // User is authenticated and has required permissions
  return (
    <AuthErrorBoundary>
      {children}
    </AuthErrorBoundary>
  );
};

// Higher-order component version
export const withProtectedRoute = <P extends object>(
  Component: React.ComponentType<P>,
  options: Omit<ProtectedRouteProps, 'children'> = {}
) => {
  const WrappedComponent = (props: P) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );

  WrappedComponent.displayName = `withProtectedRoute(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Specific role-based route components.
// MEV-566: AdminRoute is wired on /security. Other role wrappers removed —
// Builder/Trader/User share /dashboard with JWT-locked ROLE_TABS (server RBAC
// enforces privileged APIs). Re-add per-role route trees only if product splits URLs.
export const AdminRoute: React.FC<Omit<ProtectedRouteProps, 'adminOnly'>> = ({ children, ...props }) => (
  <ProtectedRoute {...props} adminOnly>
    {children}
  </ProtectedRoute>
);

// Hook for checking permissions in components (re-exported from AuthContext)
export { usePermissions } from './AuthContext';

export default ProtectedRoute;
